import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Skeleton,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import {
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  Autorenew as AutorenewIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Email as EmailIcon,
  NotificationsActive as NotificationsActiveIcon,
  NotificationsOff as NotificationsOffIcon,
  OpenInNew as OpenInNewIcon,
  PersonAddAlt1 as PersonAddAlt1Icon,
  Schedule as ScheduleIcon,
  SmartToy as SmartToyIcon,
  WhatsApp as WhatsAppIcon,
  WarningAmber as WarningAmberIcon,
  Wifi as WifiIcon,
  WifiOff as WifiOffIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { aiMailApi, aiWhatsappApi, assistantApi, whatsappApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import useAssistantNotifications from '../../hooks/useAssistantNotifications';

const priorityColor = {
  high: 'error',
  medium: 'warning',
  low: 'default',
};

const channelLabel = {
  email: 'Email',
  whatsapp: 'WhatsApp',
};

const formatDateTime = (value) => {
  if (!value) return null;
  try {
    return format(new Date(value), 'dd/MM HH:mm', { locale: it });
  } catch (_) {
    return null;
  }
};

const getTomorrowIso = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(9, 0, 0, 0);
  return tomorrow.toISOString();
};

const getPushErrorMessage = (error) => {
  const code = error?.message || error?.response?.data?.error;
  if (code === 'push_not_supported') return 'Questo browser non supporta le notifiche push PWA.';
  if (code === 'push_not_configured') return 'Configurazione push non disponibile sul server.';
  if (code === 'permission_denied') return 'Permesso notifiche negato dal browser.';
  if (code === 'permission_not_granted') return 'Permesso notifiche non concesso.';
  if (code === 'service_worker_unavailable') return 'Service worker non disponibile in questa modalità. Usa la PWA buildata.';
  if (code === 'service_worker_registration_failed') return 'Registrazione service worker fallita. Controlla che il browser non sia in modalità privata.';
  if (code === 'push_subscription_failed') return 'Registrazione push fallita. In modalità privata o incognito il browser può bloccare le notifiche push.';
  if (typeof code === 'string' && code.toLowerCase().includes('incognito')) {
    return 'Questo browser blocca le notifiche push in modalità privata o incognito.';
  }
  if (typeof code === 'string' && code.toLowerCase().includes('private')) {
    return 'Questo browser blocca le notifiche push in modalità privata o incognito.';
  }
  return error?.response?.data?.error || 'Errore notifiche assistant';
};

function SummaryCard({ label, value, color = 'default' }) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h5" fontWeight={700} color={color === 'default' ? 'text.primary' : `${color}.main`}>
          {value || 0}
        </Typography>
      </CardContent>
    </Card>
  );
}

function TaskCard({
  task,
  mode,
  currentUser,
  assignableUsers,
  onAssign,
  onApprove,
  onDone,
  onSnooze,
  onOpen,
}) {
  const metadata = task.metadata || {};
  const receivedAt = metadata.received_at || task.last_source_at || task.first_detected_at || null;
  const showApprove = Boolean(
    (task.source_channel === 'email' && task.linked_ai_action_id) ||
    (task.source_channel === 'whatsapp' && task.linked_whatsapp_action_id)
  );

  return (
    <Card variant="outlined" sx={{ mb: 1.5 }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
        <Stack spacing={1.25}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start' }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                {task.title}
              </Typography>
              {task.summary && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {task.summary}
                </Typography>
              )}
            </Box>
            <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <Chip
                size="small"
                icon={task.source_channel === 'whatsapp' ? <WhatsAppIcon /> : <EmailIcon />}
                label={channelLabel[task.source_channel] || task.source_channel}
              />
              <Chip
                size="small"
                color={priorityColor[task.priority] || 'default'}
                label={task.priority === 'high' ? 'Alta' : task.priority === 'low' ? 'Bassa' : 'Media'}
              />
            </Stack>
          </Box>

          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
            {task.customer_name && <Chip size="small" variant="outlined" color="primary" label={task.customer_name} />}
            {receivedAt && <Chip size="small" variant="outlined" icon={<ScheduleIcon />} label={`Ricevuto ${formatDateTime(receivedAt)}`} />}
            {metadata.phone_number && <Chip size="small" variant="outlined" label={metadata.phone_number} />}
          </Stack>

          {Array.isArray(task.next_steps) && task.next_steps.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                Prossimi passi
              </Typography>
              {task.next_steps.slice(0, mode === 'dashboard' ? 2 : 4).map((step, index) => (
                <Typography key={`${task.id}-step-${index}`} variant="body2" sx={{ mt: 0.25 }}>
                  • {step}
                </Typography>
              ))}
            </Box>
          )}

          {task.suggested_response && mode === 'page' && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {task.suggested_response.length > 220
                  ? `${task.suggested_response.slice(0, 220)}...`
                  : task.suggested_response}
              </Typography>
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />} onClick={() => onOpen(task)}>
              Apri
            </Button>
            {showApprove && (
              <Button size="small" variant="contained" onClick={() => onApprove(task)}>
                Approva e chiudi
              </Button>
            )}
            <Button size="small" color="success" variant="outlined" startIcon={<CheckCircleOutlineIcon />} onClick={() => onDone(task.id)}>
              Segna fatto
            </Button>
            <Button size="small" color="warning" variant="outlined" startIcon={<ScheduleIcon />} onClick={() => onSnooze(task.id)}>
              Domani
            </Button>
            {mode === 'dashboard' && !task.assigned_to_user_id && currentUser?.id && (
              <Button
                size="small"
                variant="text"
                startIcon={<PersonAddAlt1Icon />}
                onClick={() => onAssign(task.id, currentUser.id)}
              >
                Assegna a me
              </Button>
            )}
            {mode === 'page' && (
              <FormControl size="small" sx={{ minWidth: 190 }}>
                <Select
                  value={task.assigned_to_user_id || ''}
                  displayEmpty
                  onChange={(event) => onAssign(task.id, event.target.value || null)}
                >
                  <MenuItem value="">Non assegnata</MenuItem>
                  {assignableUsers.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.nome || user.username}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function AssistantChecklistSection({ mode = 'dashboard' }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [filters, setFilters] = useState({
    status: 'all',
    channel: 'all',
    mine: mode === 'dashboard',
    unassigned: false,
    priority: 'all',
  });

  const isDashboard = mode === 'dashboard';

  const refreshQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['assistant-summary'] });
    queryClient.invalidateQueries({ queryKey: ['assistant-tasks'] });
    queryClient.invalidateQueries({ queryKey: ['assistant-whatsapp-status'] });
    queryClient.invalidateQueries({ queryKey: ['assistant-push-status'] });
    queryClient.invalidateQueries({ queryKey: ['ai-dashboard-stats'] });
    queryClient.invalidateQueries({ queryKey: ['ai-pending-dashboard'] });
  }, [queryClient]);

  const handleTaskEvent = useCallback(() => {
    refreshQueries();
  }, [refreshQueries]);

  const {
    connectionStatus,
    notificationPermission,
    pushBusy,
    pushSupported,
    enablePush,
    disablePush,
    sendTestPush,
  } = useAssistantNotifications({
    onTaskEvent: handleTaskEvent,
  });
  const assistantRefetchInterval = connectionStatus === 'connected' ? 30000 : 15000;

  const { data: summary = {}, isLoading: summaryLoading } = useQuery({
    queryKey: ['assistant-summary'],
    queryFn: () => assistantApi.getSummary().then((res) => res.data?.summary || {}),
    refetchInterval: assistantRefetchInterval,
    staleTime: 30000,
  });

  const { data: tasksData, isLoading: tasksLoading } = useQuery({
    queryKey: ['assistant-tasks', mode, filters],
    queryFn: () => assistantApi.getTasks({
      limit: isDashboard ? 6 : 25,
      status: filters.status,
      channel: filters.channel,
      mine: filters.mine,
      unassigned: filters.unassigned,
      priority: filters.priority,
      includeClosed: !isDashboard && filters.status === 'all' ? 'false' : 'false',
    }).then((res) => ({ rows: res.data?.rows || [], count: res.data?.count || 0 })),
    refetchInterval: assistantRefetchInterval,
    staleTime: 30000,
  });

  const { data: assignableUsers = [] } = useQuery({
    queryKey: ['assistant-assignable-users'],
    queryFn: () => assistantApi.getAssignableUsers().then((res) => res.data?.data || []),
    staleTime: 120000,
  });

  const { data: whatsappStatus } = useQuery({
    queryKey: ['assistant-whatsapp-status'],
    queryFn: () => whatsappApi.getStatus().then((res) => res.data || {}),
    refetchInterval: assistantRefetchInterval,
    retry: 1,
    staleTime: 30000,
  });

  const { data: pushStatus } = useQuery({
    queryKey: ['assistant-push-status'],
    queryFn: () => assistantApi.getPushStatus().then((res) => res.data || {}),
    staleTime: 30000,
    retry: 1,
  });

  const showMessage = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const refreshMutation = useMutation({
    mutationFn: () => assistantApi.backfill({ channels: ['email', 'whatsapp'] }),
    onSuccess: () => {
      refreshQueries();
      showMessage('Analisi assistente avviata');
    },
    onError: (error) => showMessage(error?.response?.data?.error || 'Errore aggiornamento assistente', 'error'),
  });

  const assignMutation = useMutation({
    mutationFn: ({ taskId, assignedToUserId }) => assistantApi.assignTask(taskId, assignedToUserId),
    onSuccess: () => {
      refreshQueries();
      showMessage('Assegnazione aggiornata');
    },
    onError: (error) => showMessage(error?.response?.data?.error || 'Errore assegnazione', 'error'),
  });

  const doneMutation = useMutation({
    mutationFn: (taskId) => assistantApi.updateStatus(taskId, { status: 'done' }),
    onSuccess: () => {
      refreshQueries();
      showMessage('Task completata');
    },
    onError: (error) => showMessage(error?.response?.data?.error || 'Errore aggiornamento task', 'error'),
  });

  const snoozeMutation = useMutation({
    mutationFn: (taskId) => assistantApi.snoozeTask(taskId, getTomorrowIso()),
    onSuccess: () => {
      refreshQueries();
      showMessage('Task rimandata a domani');
    },
    onError: (error) => showMessage(error?.response?.data?.error || 'Errore snooze task', 'error'),
  });

  const approveMutation = useMutation({
    mutationFn: async (task) => {
      if (task.source_channel === 'email' && task.linked_ai_action_id) {
        await aiMailApi.approveAction(task.linked_ai_action_id);
      } else if (task.source_channel === 'whatsapp' && task.linked_whatsapp_action_id) {
        await aiWhatsappApi.approveAction(task.linked_whatsapp_action_id);
      }
      await assistantApi.updateStatus(task.id, { status: 'done' });
    },
    onSuccess: () => {
      refreshQueries();
      showMessage('Bozza approvata e task chiusa');
    },
    onError: (error) => showMessage(error?.response?.data?.error || 'Errore approvazione bozza', 'error'),
  });

  const enablePushMutation = useMutation({
    mutationFn: () => enablePush(),
    onSuccess: () => {
      refreshQueries();
      showMessage('Notifiche assistant abilitate');
    },
    onError: (error) => showMessage(getPushErrorMessage(error), 'error'),
  });

  const disablePushMutation = useMutation({
    mutationFn: () => disablePush(),
    onSuccess: () => {
      refreshQueries();
      showMessage('Notifiche assistant disattivate');
    },
    onError: (error) => showMessage(getPushErrorMessage(error), 'error'),
  });

  const testPushMutation = useMutation({
    mutationFn: () => sendTestPush(),
    onSuccess: () => showMessage('Notifica di test inviata'),
    onError: (error) => showMessage(getPushErrorMessage(error), 'error'),
  });

  const tasks = tasksData?.rows || [];
  const whatsappOnline = useMemo(() => {
    const rawStatus = whatsappStatus?.status || whatsappStatus?.data?.status;
    return ['ready', 'authenticated', 'connected'].includes(rawStatus);
  }, [whatsappStatus]);
  const realtimeConnected = connectionStatus === 'connected';
  const pushEnabled = Boolean(pushStatus?.enabled && pushStatus?.activeCount > 0);

  const openTask = (task) => {
    if (task.source_channel === 'whatsapp') {
      navigate('/crm/whatsapp', {
        state: {
          phoneNumber: task.metadata?.phone_number || task.source_ref || null,
        },
      });
      return;
    }

    navigate(task.route_path || '/crm/email/inbox');
  };

  return (
    <Card sx={{ mb: isDashboard ? 3 : 0 }}>
      <CardContent>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SmartToyIcon color="primary" />
                <Typography variant={isDashboard ? 'h6' : 'h5'} fontWeight={700}>
                  {isDashboard ? 'Checklist Assistente' : 'Assistente Personale'}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {isDashboard
                  ? 'Email e WhatsApp analizzati in task operative con priorità e ownership.'
                  : 'Backlog operativo unificato da email e WhatsApp, con quick actions e assegnazione.'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                color={realtimeConnected ? 'success' : 'default'}
                icon={realtimeConnected ? <WifiIcon /> : <WifiOffIcon />}
                label={realtimeConnected ? 'Realtime attivo' : 'Realtime in fallback'}
              />
              <Chip
                color={pushEnabled ? 'success' : 'default'}
                icon={pushEnabled ? <NotificationsActiveIcon /> : <NotificationsOffIcon />}
                label={pushEnabled ? 'Push attive' : 'Push inattive'}
              />
              {!whatsappOnline && (
                <Chip color="warning" icon={<WarningAmberIcon />} label="WhatsApp non connesso" />
              )}
              {!isDashboard && (
                <Button variant="outlined" onClick={() => navigate('/crm')}>
                  Torna alla dashboard
                </Button>
              )}
              {isDashboard && (
                <Button variant="outlined" startIcon={<OpenInNewIcon />} onClick={() => navigate('/crm/assistant')}>
                  Apri assistente
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={refreshMutation.isPending ? <AutorenewIcon /> : <AssignmentTurnedInIcon />}
                onClick={() => refreshMutation.mutate()}
                disabled={refreshMutation.isPending}
              >
                {refreshMutation.isPending ? 'Analisi in corso...' : 'Analizza ora'}
              </Button>
              {!isDashboard && pushSupported && (
                <>
                  {!pushEnabled ? (
                    <Button
                      variant="outlined"
                      startIcon={<NotificationsActiveIcon />}
                      onClick={() => enablePushMutation.mutate()}
                      disabled={pushBusy || enablePushMutation.isPending}
                    >
                      Abilita notifiche
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      color="inherit"
                      startIcon={<NotificationsOffIcon />}
                      onClick={() => disablePushMutation.mutate()}
                      disabled={pushBusy || disablePushMutation.isPending}
                    >
                      Disattiva notifiche
                    </Button>
                  )}
                  <Button
                    variant="text"
                    onClick={() => testPushMutation.mutate()}
                    disabled={!pushEnabled || testPushMutation.isPending}
                  >
                    Test push
                  </Button>
                </>
              )}
            </Box>
          </Box>

          {!isDashboard && (
            <Alert severity={pushEnabled ? 'success' : 'info'}>
              <Typography variant="body2">
                {pushEnabled
                  ? `Notifiche PWA attive. Permesso browser: ${notificationPermission}. Subscription registrate: ${pushStatus?.activeCount || 0}.`
                  : `Realtime ${realtimeConnected ? 'attivo' : 'in fallback polling'}. Permesso browser: ${notificationPermission}. Push backend: ${pushStatus?.enabled ? 'attivo' : 'non configurato'}.`}
              </Typography>
            </Alert>
          )}

          <Grid container spacing={1.5}>
            <Grid item xs={6} md={3}>
              {summaryLoading ? <Skeleton height={86} /> : <SummaryCard label="Task aperte" value={summary.total_open} color="primary" />}
            </Grid>
            <Grid item xs={6} md={3}>
              {summaryLoading ? <Skeleton height={86} /> : <SummaryCard label="Assegnate a me" value={summary.mine} color="success" />}
            </Grid>
            <Grid item xs={6} md={3}>
              {summaryLoading ? <Skeleton height={86} /> : <SummaryCard label="Alta priorità" value={summary.high_priority} color="error" />}
            </Grid>
            <Grid item xs={6} md={3}>
              {summaryLoading ? <Skeleton height={86} /> : <SummaryCard label="Non assegnate" value={summary.unassigned} color="warning" />}
            </Grid>
          </Grid>

          {!isDashboard && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <Select value={filters.status} onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}>
                  <MenuItem value="all">Stato: aperte</MenuItem>
                  <MenuItem value="open">Solo aperte</MenuItem>
                  <MenuItem value="in_progress">In lavorazione</MenuItem>
                  <MenuItem value="waiting">In attesa</MenuItem>
                  <MenuItem value="snoozed">Snoozed</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <Select value={filters.channel} onChange={(event) => setFilters((prev) => ({ ...prev, channel: event.target.value }))}>
                  <MenuItem value="all">Tutti i canali</MenuItem>
                  <MenuItem value="email">Solo email</MenuItem>
                  <MenuItem value="whatsapp">Solo WhatsApp</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select value={filters.priority} onChange={(event) => setFilters((prev) => ({ ...prev, priority: event.target.value }))}>
                  <MenuItem value="all">Tutte le priorità</MenuItem>
                  <MenuItem value="high">Alta priorità</MenuItem>
                  <MenuItem value="medium">Media priorità</MenuItem>
                  <MenuItem value="low">Bassa priorità</MenuItem>
                </Select>
              </FormControl>
              <Button variant={filters.mine ? 'contained' : 'outlined'} onClick={() => setFilters((prev) => ({ ...prev, mine: !prev.mine, unassigned: prev.mine ? prev.unassigned : false }))}>
                Assegnate a me
              </Button>
              <Button variant={filters.unassigned ? 'contained' : 'outlined'} onClick={() => setFilters((prev) => ({ ...prev, unassigned: !prev.unassigned, mine: prev.unassigned ? prev.mine : false }))}>
                Non assegnate
              </Button>
            </Box>
          )}

          <Divider />

          {tasksLoading ? (
            <Stack spacing={1.5}>
              <Skeleton height={132} />
              <Skeleton height={132} />
              <Skeleton height={132} />
            </Stack>
          ) : tasks.length === 0 ? (
            <Alert severity="success">
              Nessuna attività aperta. L'assistente non ha rilevato richieste operative da gestire.
            </Alert>
          ) : (
            <Box>
              {tasks.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  mode={mode}
                  currentUser={user}
                  assignableUsers={assignableUsers}
                  onAssign={(taskId, assignedToUserId) => assignMutation.mutate({ taskId, assignedToUserId })}
                  onApprove={(taskToApprove) => approveMutation.mutate(taskToApprove)}
                  onDone={(taskId) => doneMutation.mutate(taskId)}
                  onSnooze={(taskId) => snoozeMutation.mutate(taskId)}
                  onOpen={openTask}
                />
              ))}
            </Box>
          )}
        </Stack>
      </CardContent>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((state) => ({ ...state, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          variant="filled"
          severity={snackbar.severity}
          onClose={() => setSnackbar((state) => ({ ...state, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
}